/**
 * FLIP-273. The client's view of the Realtime voice contract that shipped in FLIP-255 / FLIP-257.
 *
 * Hand-written rather than generated: the NSwag client has no Realtime methods, and every other
 * Nova DTO on this side is mirrored by hand for the same reason (see nova-companion-chat.service.ts).
 * Field names match the server's [JsonPropertyName] attributes exactly - these are the wire names,
 * not a re-modelling of them.
 */

/**
 * What the browser is given to open a session, and deliberately nothing else.
 *
 * There is no instructions field and there is not meant to be one. The ~19k-token prompt and the
 * session's tool access are baked into the minted secret server-side, which is what makes this a
 * session-scoped credential the page can spend but cannot renegotiate. Do not add a way to send
 * instructions from here.
 */
export interface NovaRealtimeSession {
  /** The ephemeral `ek_...` secret. Used once to establish the peer connection, then spent. */
  clientSecret: string;
  /** When the secret stops being usable, UTC, as an ISO string. */
  expiresAt: string;
  /**
   * The conversation these spoken turns belong to, resolved once and pinned for the session.
   *
   * Carried here so the relay ticket can echo it back, keeping spoken turns in the same
   * conversation the typed ones use. This service does not otherwise read it.
   */
  conversationId: string;
  /** Which model the session was minted against. Reported for support, not chosen by the client. */
  model: string;
  /** The session's wall-clock ceiling in minutes. */
  maxSessionMinutes: number;
  /** Server-assigned id for this session; what EndRealtimeSession and the relay are keyed on. */
  sessionId: string;
}

/**
 * Why a mint was refused. Stable strings - branch on these, never on `message`.
 *
 * Typed as a union with a `string` escape so an unrecognised reason from a newer server does not
 * fail to parse; callers should treat anything they do not recognise as a plain refusal.
 */
export type NovaRealtimeRefusalReason = "concurrent_session" | "hourly_limit" | string;

/**
 * A 429 refusal body. Distinct from an outage on purpose: "you already have a session open" and
 * "Nova is down" call for completely different things from the person reading them.
 */
export interface NovaRealtimeRefusal {
  reason: NovaRealtimeRefusalReason;
  /** Human-readable and safe to show as-is. */
  message: string;
  /** Seconds until a retry could succeed; 0 when that cannot be computed. */
  retryAfterSeconds: number;
}

/**
 * How a session ended, as reported to EndRealtimeSession.
 *
 * `unknown` is not in this union because the client never sends it: it is what the server records
 * when a reason is missing or unrecognised, which is what an unload close deliberately produces.
 */
export type NovaRealtimeEndReason = "closed" | "error" | "expired";

/** What the server says about a close. Both `closed` values are successes. */
export interface NovaRealtimeEndSessionResponse {
  closed: boolean;
  durationSeconds: number;
}

/**
 * Where the transport is.
 *
 * `ending` exists so a caller can tell a session being torn down from one that never started -
 * during teardown the audio is already gone but the slot is not yet released.
 */
export type NovaRealtimeState = "idle" | "connecting" | "connected" | "ending" | "failed";

/**
 * One inbound message from the `oai-events` data channel, parsed.
 *
 * Left as an open shape on purpose. A session emits dozens of event types and this ticket does not
 * interpret any of them - the relay and tool-call tickets narrow this by `type` where they care.
 */
export interface NovaRealtimeServerEvent {
  type: string;
  [key: string]: unknown;
}

/**
 * The outcome of trying to start a session.
 *
 * A discriminated union rather than an exception, because the cases are all ordinary and the
 * caller has to tell them apart to say anything useful: `refused` is the user's own session or
 * allowance and is recoverable by waiting, `unavailable` is ours being broken, `cancelled` is the
 * user hanging up before the connect finished - their own doing, and nothing to report - and only
 * `started` means there is audio.
 */
/**
 * FLIP-285. How long each phase of the connect took, in milliseconds.
 *
 * WHY THE PARTS DO NOT SUM TO `totalMs`. The mint and the microphone are started together, so
 * their durations OVERLAP - `totalMs` is roughly `max(mintMs, microphoneMs) + offerMs + sdpMs`,
 * not the sum of all four. That is the point of the parallelism, and a reader comparing the
 * numbers should be able to see it rather than conclude the measurement is broken.
 *
 * Measured on the client because it is the only place that can see all three: the microphone and
 * the SDP exchange never touch our server.
 */
export interface NovaRealtimeConnectTimings {
  /** POST /api/Nova/StartRealtimeSession — our server, including its own call to OpenAI. */
  mintMs: number;
  /** getUserMedia — opening the audio device. Runs alongside the mint. */
  microphoneMs: number;
  /** createOffer + setLocalDescription. Local, and expected to be small. */
  offerMs: number;
  /** The SDP exchange with OpenAI. */
  sdpMs: number;
  /** Wall clock for the whole connect, which is the number the user actually waits. */
  totalMs: number;
}

export type NovaRealtimeStartResult =
  | { outcome: "started"; session: NovaRealtimeSession; timings: NovaRealtimeConnectTimings }
  | { outcome: "refused"; refusal: NovaRealtimeRefusal }
  | { outcome: "unavailable"; message: string }
  | { outcome: "cancelled" };

/**
 * FLIP-274. The four event types the relay endpoint accepts, and deliberately only these.
 *
 * Mirrors `NovaRealtimeEventTypes` on the server. Named here rather than spelled inline at the
 * call site so the client's filter and the server's switch cannot drift apart on a string: an
 * event type that does not match is not an error on either side, it is a turn that silently never
 * gets written, which is the failure this whole ticket exists to prevent.
 *
 * A session emits dozens of other types per turn - audio deltas, VAD edges, rate-limit updates -
 * and relaying them would put a request on our server for every fragment of speech, which is the
 * cost the Realtime migration exists to avoid. They are read where they help (see the barge-in
 * bookkeeping in the relay service) but never relayed.
 */
export const NovaRealtimeRelayedEventTypes = {
  /** The user's speech, transcribed. Records the user turn. */
  InputTranscriptionCompleted: "conversation.item.input_audio_transcription.completed",
  /** Nova's reply, finished. Records the assistant turn. */
  ResponseDone: "response.done",
  /**
   * Barge-in. CLIENT-ORIGINATED, unlike the other three.
   *
   * The server has no audio, so it cannot know where the reply was cut - this event carries the
   * text actually heard, and on the server it is what writes the assistant turn. The
   * `response.done` that follows is then refused by the item id they share.
   */
  ItemTruncate: "conversation.item.truncate",
  /** The model called a tool and is waiting for an output only the server can compose. */
  FunctionCallArgumentsDone: "response.function_call_arguments.done",
} as const;

/** One of the four relayed types. */
export type NovaRealtimeRelayedEventType =
  typeof NovaRealtimeRelayedEventTypes[keyof typeof NovaRealtimeRelayedEventTypes];

/**
 * One relayed event, as `POST /api/Nova/RealtimeEvent` expects it.
 *
 * One flat shape rather than a type per event, matching the server's `NovaRealtimeEventRequest`:
 * `type` says which of the optional fields matter. Field names are the wire names from the
 * server's [JsonPropertyName] attributes, not a re-modelling of them.
 */
export interface NovaRealtimeEventRequest {
  /** The session, checked against the server's ledger rather than trusted. */
  sessionId: string;
  /**
   * The conversation the client believes it is in - a FALLBACK, not the authority.
   *
   * The server pins the authoritative id at mint time and uses this only when its ledger has no
   * entry, which happens when the API restarted mid-session. Sent at all so a deploy does not
   * silently drop a live session's turns on the floor.
   */
  conversationId: string;
  type: NovaRealtimeRelayedEventType;
  /**
   * The Realtime `item_id`, and the key every write is made idempotent on.
   *
   * Load-bearing for the retry policy: a retried POST reuses this unchanged, which is what makes
   * the server's dedupe hold instead of appending a second copy of something said once.
   */
  itemId?: string;
  /** The user's transcript, Nova's reply, or - on a truncate - the part that was actually heard. */
  transcript?: string;
  /** The function the model called, on a function-call event. */
  functionName?: string;
  /** The call's arguments as the model produced them, relayed as the string they arrive as. */
  arguments?: string;
  /** The Realtime `call_id`, so the tool-call ticket can address its output to the right call. */
  callId?: string;
  /**
   * Which user turn a function call belongs to: a counter bumped each time the user starts
   * speaking. The server holds an app-guidance turn to one call, and cannot tell turns apart
   * without it. Absent on anything that is not a model's call, such as a prefetch.
   */
  turnId?: number;
  /**
   * "mobile" or "desktop": which layout the user is in, on a function call, so a route is answered
   * with the one set of directions that applies. Absent when unknown, and the server gives both.
   */
  device?: string;
}

/**
 * B6. One step of a Nova-guided walkthrough. Mirrors the server's `NovaWalkthroughStep`
 * (`[JsonPropertyName]`-annotated there, so these are the wire names).
 *
 * `nav`/`branch` stay `unknown`, matching the server's own `JsonElement?` - their shape isn't
 * settled yet, fixed by whatever eventually reads them rather than by this catalogue record.
 */
export interface NovaWalkthroughStep {
  id: string;
  walkthrough: string;
  order: number;
  /** One of the five app areas, or null for a step on a route or in a global overlay. */
  area: string | null;
  /**
   * A client route such as "/settings" for a step outside the five areas, otherwise null. A step
   * with neither an area nor a route is deliberate: it happens in a global overlay (Nova
   * Companion, dictation, voice chat) independent of the page beneath it.
   */
  route?: string | null;
  screen: string;
  instruction: string;
  expect: string;
  nav?: unknown;
  branch?: unknown;
}

/**
 * Where this browser has got to in the walkthrough it holds, sent with each typed message so the
 * server can tell the model which step the user is on and answer the walkthrough tools. Ids only:
 * the step text always comes from the catalogue, so nothing sent here is put in front of the model.
 * Mirrors the server's `NovaWalkthroughState`.
 */
export interface NovaWalkthroughState {
  walkthroughId: string;
  /** The step most recently delivered to the model. */
  currentStepId: string;
  /** The next step, once the user has finished the current one and it is waiting on `next_step`. */
  armedStepId: string | null;
  paused: boolean;
}

/**
 * A change to the walkthrough the model asked for in a typed turn, for the browser to apply to the
 * state it owns. The server changes nothing itself. Mirrors `NovaWalkthroughEvent`.
 */
export interface NovaWalkthroughEvent {
  /** next_step, pause_walkthrough, resume_walkthrough, skip_to or end_walkthrough. */
  type: string;
  stepId?: string | null;
}

/** One location's steps of a walkthrough, from `POST api/Nova/WalkthroughSlice`. */
export interface NovaWalkthroughSliceResponse {
  walkthroughSteps?: NovaWalkthroughStep[];
  walkthroughNextArea?: string | null;
}

/** The location value of a step in a global overlay - the server's `NovaWalkthroughStep.OverlayLocation`. */
export const WALKTHROUGH_OVERLAY_LOCATION = "overlay";

/**
 * Where a step happens as one comparable key: its area, else its route, else the overlay. The
 * server slices and prefetches a walkthrough in contiguous runs of this key, so the client has to
 * compare in it too - comparing `area` alone would treat every Settings, Community, Help and Nova
 * step as belonging nowhere.
 */
export function walkthroughStepLocation(step: Pick<NovaWalkthroughStep, "area" | "route">): string {
  return step.area ?? step.route ?? WALKTHROUGH_OVERLAY_LOCATION;
}

/**
 * What the relay tells the client about one event. Mirrors `NovaRealtimeEventResponse`.
 *
 * Every field is optional on this side: a response from a newer server that has grown a field is
 * parsed rather than rejected, and an older one that has not is not assumed to have sent it.
 */
export interface NovaRealtimeEventResponse {
  /**
   * Whether this call wrote a turn. False for an event that records nothing, and false for a
   * duplicate that was already recorded - which is a SUCCESS, not a failure.
   */
  recorded?: boolean;
  /** True when the turn was already on file under this item id. Also a success. */
  duplicate?: boolean;
  /** The validated navigation target for a `navigate_to` call. Consumed by the tool-call ticket. */
  nav?: unknown;
  /** The JSON to send back as the call's `function_call_output`. Tool-call ticket. */
  functionCallOutput?: string;
  /** The `call_id` that output belongs to. Tool-call ticket. */
  callId?: string;
  /** True when the model raised a safety escalation on this event, so crisis support is surfaced. */
  safetyEscalationTriggered?: boolean;
  /**
   * B6. The browser-only side-band from a `begin_walkthrough` call: the ordered steps for the
   * walkthrough's current area.
   *
   * NEVER FORWARDED INTO THE REALTIME CONVERSATION - that is the whole point of it being a
   * side-band. Consumed by `NovaWalkthroughService`.
   */
  walkthroughSteps?: NovaWalkthroughStep[];
  /**
   * B6. The area the walkthrough moves into after this one, if any - a hint for
   * `NovaWalkthroughService` to prefetch, never that area's steps. Null/absent on the
   * walkthrough's last area, or on any response that is not a `begin_walkthrough` answer.
   */
  walkthroughNextArea?: string | null;
  /**
   * True when the client must end the voice session now.
   *
   * Set after enough consecutive relay failures that the conversation is demonstrably going
   * unrecorded. Continuing past this produces exactly what this ticket prevents: a long
   * conversation that left no trace, discovered by the user as an empty scrollback.
   */
  sessionTerminated?: boolean;
  /** What to tell the person, when there is something to tell them. Safe to show as-is. */
  message?: string;
}

/** Which side of the conversation a relayed turn came from. */
export type NovaRelayedTurnRole = "user" | "assistant";

/**
 * A turn this service has successfully put on the server's record.
 *
 * Exposed so the voice-mode ticket has a seam to render live scrollback from. Emitted for a
 * duplicate as well as a fresh write, because from the caller's point of view both mean the same
 * thing: this turn is on file.
 */
export interface NovaRelayedTurn {
  role: NovaRelayedTurnRole;
  text: string;
  itemId: string;
  /** True when the assistant turn was cut short by the user speaking over it. */
  truncated: boolean;
}

// ── Tool calls (FLIP-275) ──

/**
 * The tools a Realtime session may call.
 *
 * Defined server-side in `OpenAiNovaRealtimeSessionClient`; named here so the switch that acts on
 * a call and that definition cannot drift apart on a spelling.
 */
export const NovaRealtimeFunctions = {
  NavigateTo: "navigate_to",
  RaiseSafetyEscalation: "raise_safety_escalation",
  // B6. Only BeginWalkthrough has a server handler in v1 (NovaRealtimeRelayService). The other
  // five are registered server-side (OpenAiNovaRealtimeSessionClient.BuildTools) so the model may
  // call them, but nothing on this side of the relay acts on them yet - that's
  // NovaWalkthroughService, a later punch-list item. Named here now so that work has these to
  // import rather than re-deriving the spelling.
  BeginWalkthrough: "begin_walkthrough",
  NextStep: "next_step",
  PauseWalkthrough: "pause_walkthrough",
  ResumeWalkthrough: "resume_walkthrough",
  SkipTo: "skip_to",
  EndWalkthrough: "end_walkthrough",
} as const;

/**
 * One relayed event and what the server said about it.
 *
 * WHY THE REQUEST IS CARRIED TOO. `NovaRealtimeEventResponse` says what the server resolved but
 * not what it was asked, and the tool-call ticket needs both: `functionName` to tell `navigate_to`
 * from `raise_safety_escalation`, and `arguments` to label a toast the server could not resolve.
 * Correlating the two streams by `callId` on the other side would re-introduce exactly the
 * ordering fragility the relay's queue exists to remove.
 *
 * `response` is null when the relay could not answer at all - every retry failed, or the session
 * went away underneath it. That is a REAL CASE with a required behaviour, not a detail: a function
 * call the model is waiting on must still be answered with something, or the session hangs for the
 * rest of its life. It is emitted rather than swallowed so somebody can do that.
 */
export interface NovaRealtimeRelayExchange {
  request: NovaRealtimeEventRequest;
  response: NovaRealtimeEventResponse | null;
}

/**
 * What a completed tool call leaves for the UI to render.
 *
 * Separate from the round trip on purpose. Answering the model is mandatory and must happen
 * whether or not anything is on screen; surfacing crisis resources is only possible when something
 * is. Voice offers no navigation link, so a safety escalation is the only outcome there is.
 */
export interface NovaRealtimeToolCallOutcome {
  /** Whether crisis resources should be surfaced for this call. */
  safetyEscalationTriggered: boolean;
}
